import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface CaseDetail {
  id: number;
  titleKey: string;
  typeKey: string;
  descKey: string;
  tags: string[];
  img: string;
  year?: string;
  featured?: boolean;
  clientKey?: string;
  challengeKey?: string;
  solutionKey?: string;
  resultsKeys?: string[];
  gallery?: string[];
}

interface CaseModalProps {
  caseItem: CaseDetail | null;
  onClose: () => void;
  getTitle: (c: CaseDetail) => string;
  getType: (c: CaseDetail) => string;
  getDesc: (c: CaseDetail) => string;
  getText: (key: string) => string;
}

export default function CaseModal({ caseItem, onClose, getTitle, getType, getDesc, getText }: CaseModalProps) {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (caseItem) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [caseItem]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!caseItem) return null;

  const lang = i18n.language === 'zh' ? 'zh' : 'en';

  const labels = {
    client: lang === 'zh' ? '客户' : 'Client',
    year: lang === 'zh' ? '年份' : 'Year',
    type: lang === 'zh' ? '项目类型' : 'Project Type',
    challenge: lang === 'zh' ? '项目挑战' : 'Challenge',
    solution: lang === 'zh' ? '解决方案' : 'Solution',
    results: lang === 'zh' ? '项目成果' : 'Key Results',
    techStack: lang === 'zh' ? '技术栈' : 'Tech Stack',
    close: lang === 'zh' ? '关闭' : 'Close',
    consult: lang === 'zh' ? '咨询类似项目' : 'Consult Similar Project',
  };

  const client = caseItem.clientKey ? getText(caseItem.clientKey) : '';
  const challenge = caseItem.challengeKey ? getText(caseItem.challengeKey) : '';
  const solution = caseItem.solutionKey ? getText(caseItem.solutionKey) : '';
  const results = caseItem.resultsKeys ? caseItem.resultsKeys.map((k) => getText(k)) : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative bg-white rounded-3xl overflow-hidden w-full max-w-3xl max-h-[90vh] flex flex-col animate-modal-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Hero image */}
        <div className="relative w-full h-56 md:h-72 shrink-0 overflow-hidden">
          <img
            src={caseItem.img}
            alt={getTitle(caseItem)}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-500/90 text-white text-xs font-semibold mb-2">
              {getType(caseItem)}
            </span>
            <h2 className="text-white text-2xl md:text-3xl font-bold leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
              {getTitle(caseItem)}
            </h2>
          </div>
          {caseItem.featured && (
            <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-amber-500 text-white text-xs font-bold">
              {lang === 'zh' ? '精选' : 'Featured'}
            </div>
          )}
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 p-6 md:p-8">
          {/* Meta row */}
          <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-[#f0ebe0]">
            {client && (
              <div>
                <div className="text-[#999] text-xs mb-1">{labels.client}</div>
                <div className="text-[#1e3a4a] text-sm font-semibold">{client}</div>
              </div>
            )}
            {caseItem.year && (
              <div>
                <div className="text-[#999] text-xs mb-1">{labels.year}</div>
                <div className="text-[#1e3a4a] text-sm font-semibold">{caseItem.year}</div>
              </div>
            )}
            <div>
              <div className="text-[#999] text-xs mb-1">{labels.type}</div>
              <div className="text-[#1e3a4a] text-sm font-semibold">{getType(caseItem)}</div>
            </div>
          </div>

          {/* Description */}
          <p className="text-[#555] text-sm leading-relaxed mb-6">{getDesc(caseItem)}</p>

          {/* Challenge */}
          {challenge && (
            <div className="mb-6">
              <h4 className="text-[#1e3a4a] font-bold text-sm mb-2 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <i className="ri-question-line text-xs"></i>
                </span>
                {labels.challenge}
              </h4>
              <p className="text-[#666] text-sm leading-relaxed pl-7">{challenge}</p>
            </div>
          )}

          {/* Solution */}
          {solution && (
            <div className="mb-6">
              <h4 className="text-[#1e3a4a] font-bold text-sm mb-2 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#1e3a4a]/10 text-[#1e3a4a]">
                  <i className="ri-lightbulb-line text-xs"></i>
                </span>
                {labels.solution}
              </h4>
              <p className="text-[#666] text-sm leading-relaxed pl-7">{solution}</p>
            </div>
          )}

          {/* Results */}
          {results.length > 0 && (
            <div className="mb-6">
              <h4 className="text-[#1e3a4a] font-bold text-sm mb-3 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 text-green-600">
                  <i className="ri-bar-chart-line text-xs"></i>
                </span>
                {labels.results}
              </h4>
              <ul className="pl-7 space-y-2">
                {results.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#555]">
                    <i className="ri-check-line text-green-500 mt-0.5 shrink-0"></i>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tech Stack */}
          <div className="mb-8">
            <h4 className="text-[#1e3a4a] font-bold text-sm mb-3 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#f5f0e8] text-[#1e3a4a]">
                <i className="ri-code-s-slash-line text-xs"></i>
              </span>
              {labels.techStack}
            </h4>
            <div className="flex flex-wrap gap-2 pl-7">
              {caseItem.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 rounded-full bg-[#f5f0e8] text-[#1e3a4a] text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <a
            href="https://wa.me/6596628319"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-[#1e3a4a] hover:bg-[#2d5568] text-white font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-base"></i>
            <span>{labels.consult}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
